import { useThreadNewTracking } from "@/features/sidebar/projects/hooks/useThreadNewTracking";
import { isDefaultTitle } from "@/features/sidebar/projects/utils/projectHelpers";
import { useToast } from "@/hooks/useToast";
import { SupabaseThreadUtils } from "@/lib/db/utils/supabase/threads";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { supabase } from "@/lib/supabase";
import { refreshThreadsAndCleanup } from "@/lib/utils/threadCleanupUtils";
import {
  deleteThreadWithQueue,
  isThreadBeingDeleted,
} from "@/lib/utils/threadDeletionQueue";
import { useAuthStore, useThreadStore } from "@/store";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Hook for managing history thread actions
 */
export function useHistoryActions({
  workspaces,
  setWorkspaces,
  markThreadAsNew,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentThreadId } = useThreadStore();
  const { toast } = useToast();

  // Thread renaming dialog state
  const [threadRenameDialogOpen, setThreadRenameDialogOpen] = useState(false);
  const [threadToRename, setThreadToRename] = useState(null);
  const [newThreadName, setNewThreadName] = useState("");
  const [isRenamingThread, setIsRenamingThread] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Track previous thread titles for animation
  const [previousThreadTitles, setPreviousThreadTitles] = useState(new Map());
  const { isThreadNew } = useThreadNewTracking();

  // Track thread title changes for animation
  useEffect(() => {
    const { threads } = useThreadStore.getState();
    if (threads.length > 0) {
      let hasChanges = false;
      const newTitleMap = new Map();

      threads.forEach((thread) => {
        const currentTitle = thread.title;
        const previousTitle = previousThreadTitles.get(thread.id);

        newTitleMap.set(thread.id, currentTitle);

        if (
          previousTitle !== undefined &&
          isDefaultTitle(previousTitle) &&
          !isDefaultTitle(currentTitle) &&
          !isThreadNew(thread.id)
        ) {
          markThreadAsNew(thread.id, currentTitle);
        }

        if (previousTitle !== currentTitle) {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setPreviousThreadTitles(newTitleMap);
      }
    }
  }, [isThreadNew, markThreadAsNew]);

  // Handle thread moved to history from localStorage
  useEffect(() => {
    const { threads } = useThreadStore.getState();
    const movedToHistoryFlag = localStorage.getItem("threadMovedToHistory");
    if (movedToHistoryFlag) {
      const thread = threads.find((t) => t.id === movedToHistoryFlag);
      if (thread && !isDefaultTitle(thread.title)) {
        markThreadAsNew(
          movedToHistoryFlag,
          thread.title || `Chat ${movedToHistoryFlag.substring(0, 6)}...`
        );
      }
      localStorage.removeItem("threadMovedToHistory");
    }
  }, [markThreadAsNew]);

  const handleThreadDelete = useCallback(
    async (thread) => {
      // Check if thread is already being deleted
      if (isThreadBeingDeleted(thread.id)) {
        return;
      }

      const isCurrentThread = thread.id === currentThreadId;

      // Find workspaces that contain this thread
      const workspacesToUpdate = workspaces.filter(
        (workspace) =>
          workspace.workspace_threads &&
          workspace.workspace_threads.includes(thread.id)
      );

      // Extract vector store ID for cleanup
      const vectorStoreId =
        thread.metadata?.vector_store_id ||
        thread.vector_store_id ||
        thread.metadata?.assistant_id?.vector_store_id ||
        thread.metadata?.assistant_id?.metadata?.vector_store_id;

      // Use the deletion queue system
      const success = await deleteThreadWithQueue(thread.id, {
        isCurrentThread,
        vectorStoreId,
        originalThread: thread,
        navigate: isCurrentThread ? navigate : null,
        setWorkspaces,
        workspacesToUpdate,
        showErrorToast: (message) => {
          alert(message); // Using alert for consistency with existing code
        },
        onSuccess: async (threadId) => {
          // Clear localStorage items if this was the current thread
          if (isCurrentThread) {
            localStorage.removeItem("lastVisitedThreadId");
            localStorage.removeItem("pendingNewThreadId");
            localStorage.removeItem("last_workspace_thread_id");
          }

          try {
            // Refresh threads list and cleanup any untitled chats
            await refreshThreadsAndCleanup({
              removeUntitledChats: true,
              onProgress: (progress) => {},
            });
          } catch (refreshError) {
            console.warn(
              "Error during thread refresh and cleanup:",
              refreshError
            );
          }

          // Remove from all workspaces that contained this thread
          if (workspacesToUpdate.length > 0) {
            try {
              await Promise.all(
                workspacesToUpdate.map(async (workspace) => {
                  try {
                    await SupabaseWorkspaceUtils.removeThreadFromWorkspace(
                      workspace.id,
                      threadId
                    );
                  } catch (workspaceError) {
                    console.error("Error updating workspace:", workspaceError);
                  }
                })
              );
            } catch (workspaceUpdateError) {
              console.error("Error updating workspaces:", workspaceUpdateError);
            }
          }
        },
        onError: (threadId, error) => {
          console.error(`Failed to delete thread ${threadId}:`, error);
          alert("Failed to delete thread. Please try again.");
        },
      });

      if (!success) {
        alert("Failed to queue thread for deletion. Please try again.");
      }
    },
    [currentThreadId, workspaces, setWorkspaces, navigate]
  );

  const startRenaming = useCallback((thread) => {
    setOpenDropdownId(null);
    setThreadToRename(thread);
    setNewThreadName(thread.title || "");
    setThreadRenameDialogOpen(true);
  }, []);

  const handleThreadRenameSubmit = useCallback(async () => {
    if (!threadToRename || !newThreadName.trim()) return;

    try {
      setIsRenamingThread(true);

      const { data, error } = await supabase
        .from("threads")
        .update({
          title: newThreadName.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", threadToRename.id)
        .select("*")
        .single();

      if (error) {
        console.error("Error updating thread title in Supabase:", error);
        throw error;
      }

      useThreadStore.setState((state) => {
        const threadIsCurrentThread =
          state.currentThread?.id === threadToRename.id;

        return {
          threads: state.threads.map((t) =>
            t.id === threadToRename.id
              ? {
                  ...t,
                  title: newThreadName.trim(),
                  updated_at: new Date().toISOString(),
                }
              : t
          ),
          currentThread: threadIsCurrentThread
            ? {
                ...state.currentThread,
                title: newThreadName.trim(),
                updated_at: new Date().toISOString(),
              }
            : state.currentThread,
          currentThreadTitle: threadIsCurrentThread
            ? newThreadName.trim()
            : state.currentThreadTitle,
        };
      });

      setThreadRenameDialogOpen(false);
      setThreadToRename(null);
      setNewThreadName("");
    } catch (error) {
      console.error("Error renaming thread:", error);
    } finally {
      setIsRenamingThread(false);
    }
  }, [threadToRename, newThreadName]);

  const handleThreadRenameCancel = useCallback(() => {
    setThreadRenameDialogOpen(false);
    setThreadToRename(null);
    setNewThreadName("");
  }, []);

  const handleThreadSelect = useCallback(
    async (localThread) => {
      try {
        // Clear intentionalProjectNavigation flag if set (user is explicitly selecting a thread)
        const intentionalProjectNav = localStorage.getItem(
          "intentionalProjectNavigation"
        );
        if (intentionalProjectNav === "true") {
          localStorage.removeItem("intentionalProjectNavigation");
        }

        if (localThread?.id) {
          setIsLoading(true);

          // Check if we're coming from a project page - if so, use replace to avoid sidebar reload
          const isFromProjectPage = location.pathname.startsWith("/projects/");
          const navigateOptions = isFromProjectPage
            ? { replace: true, state: { preventReload: true } }
            : { state: { preventReload: true } };

          useThreadStore.setState({
            currentThread: localThread,
            currentThreadId: localThread.id,
            currentThreadTitle: localThread.title || "Untitled Chat",
            threadSwitchInProgress: true,
            preventNewMessageClear: true,
            isLoading: true,
          });

          const now = Date.now();
          localStorage.setItem("threadSelectedFromSidebar", localThread.id);
          localStorage.setItem("threadSelectionTimestamp", now.toString());
          localStorage.setItem("lastVisitedThreadId", localThread.id);

          // Navigate first to prevent sidebar reload
          // ChatInterface will load messages when it mounts for this thread
          navigate(`/chat/${localThread.id}`, navigateOptions);

          setTimeout(() => {
            setIsLoading(false);

            // Force scroll to bottom after navigation
            setTimeout(() => {
              const event = new CustomEvent("force-scroll-to-bottom", {
                detail: {
                  threadId: localThread.id,
                  source: "history-navigation",
                },
              });
              window.dispatchEvent(event);
            }, 300);
          }, 50);
        } else {
          console.error(
            "[NavChatHistory] Invalid thread data for selection:",
            localThread
          );
          setIsLoading(false);
        }
      } catch (error) {
        console.error(
          "[NavChatHistory] Error handling thread selection:",
          error
        );
        setIsLoading(false);
      }
    },
    [navigate, location.pathname]
  );

  const handleToggleBookmark = useCallback(
    async (thread) => {
      try {
        setIsLoading(true);

        const isBookmarked = thread.metadata?.bookmarked || false;

        const updatedMetadata = {
          ...(thread.metadata || {}),
          bookmarked: !isBookmarked,
          bookmark_updated_at: new Date().toISOString(),
        };

        const { thread: updatedThread, error } =
          await SupabaseThreadUtils.updateThreadMetadata({
            supabaseThreadId: thread.id,
            metadata: updatedMetadata,
          });

        if (error) {
          console.error("Error toggling bookmark:", error);
          throw error;
        }

        useThreadStore.setState((state) => {
          const threadIsCurrentThread = state.currentThread?.id === thread.id;

          return {
            threads: state.threads.map((t) =>
              t.id === thread.id ? { ...t, metadata: updatedMetadata } : t
            ),
            currentThread: threadIsCurrentThread
              ? { ...state.currentThread, metadata: updatedMetadata }
              : state.currentThread,
          };
        });

        toast({
          title: !isBookmarked ? "Chat pinned" : "Chat unpinned",
          description: !isBookmarked
            ? "Chat has been pinned to the top"
            : "Chat has been unpinned",
        });
      } catch (error) {
        console.error("Error toggling bookmark:", error);
        toast({
          title: "Error",
          description: "Failed to update pin status",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [toast]
  );

  const handleThreadClone = useCallback(
    async (thread) => {
      try {
        setIsLoading(true);

        const { user, currentWorkspaceId } = useAuthStore.getState();

        const { data: originalThread, error: threadError } = await supabase
          .from("threads")
          .select("*")
          .eq("id", thread.id)
          .single();

        if (threadError) {
          throw new Error(`Failed to fetch thread: ${threadError.message}`);
        }

        const workspaceId = originalThread.workspace_id || currentWorkspaceId;

        if (!workspaceId) {
          throw new Error(
            "No workspace ID available. Please select a workspace first."
          );
        }

        const { data: originalMessages, error: messagesError } = await supabase
          .from("thread_messages")
          .select("*")
          .eq("thread_id", thread.id)
          .order("created_at", { ascending: true });

        if (messagesError) {
          throw new Error(`Failed to fetch messages: ${messagesError.message}`);
        }

        const newThreadTitle = `Clone of ${thread.title || "Untitled Chat"}`;
        const timestamp = new Date().toISOString();

        const cloneId = `clone-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

        const threadMetadata = {
          ...originalThread.metadata,
          cloned_from: thread.id,
          cloned_at: timestamp,
          original_title: thread.title,
          clone_id: cloneId,
          is_team: false,
        };

        const { data: newThread, error: newThreadError } = await supabase
          .from("threads")
          .insert({
            title: newThreadTitle,
            user_id: originalThread.user_id,
            workspace_id: workspaceId,
            metadata: threadMetadata,
            created_at: timestamp,
            updated_at: timestamp,
            provider: originalThread.provider || "openai",
            status: "active",
            is_team: false,
            is_shared: false,
          })
          .select("*")
          .single();

        if (newThreadError) {
          throw new Error(
            `Failed to create new thread: ${newThreadError.message}`
          );
        }

        let insertedMessages = [];
        if (originalMessages && originalMessages.length > 0) {
          const messagesToInsert = originalMessages.map((message) => {
            const newMessageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

            return {
              thread_id: newThread.id,
              message_id: newMessageId,
              role: message.role,
              content: message.content,
              metadata: message.metadata || {},
              created_at: timestamp,
              workspace_id: workspaceId,
              user_id: originalThread.user_id,
              provider: message.provider || "openai",
              file_ids: message.file_ids || [],
              tools_count: message.tools_count || 0,
              attachments: message.attachments,
              annotations: message.annotations,
              tool_calls: message.tool_calls,
            };
          });

          for (let i = 0; i < messagesToInsert.length; i += 100) {
            const batch = messagesToInsert.slice(i, i + 100);
            const { data: insertedBatch, error: messagesInsertError } =
              await supabase.from("thread_messages").insert(batch).select("*");

            if (messagesInsertError) {
              console.error(
                `Error inserting messages batch ${i / 100 + 1}:`,
                messagesInsertError
              );
            } else if (insertedBatch) {
              insertedMessages = [...insertedMessages, ...insertedBatch];
            }
          }

          const { error: threadUpdateError } = await supabase
            .from("threads")
            .update({
              message_count: originalMessages.length,
              last_message_at: timestamp,
              last_activity: timestamp,
            })
            .eq("id", newThread.id);

          if (threadUpdateError) {
            console.error(
              "Error updating thread message count:",
              threadUpdateError
            );
          }
        }

        const chronologicallySortedMessages = insertedMessages.sort((a, b) => {
          const aDate = new Date(a.created_at).getTime();
          const bDate = new Date(b.created_at).getTime();
          return aDate - bDate;
        });

        useThreadStore.setState((state) => {
          return {
            threads: [newThread, ...state.threads],
            messages: chronologicallySortedMessages,
          };
        });

        markThreadAsNew(newThread.id, newThreadTitle);

        handleThreadSelect(newThread);
      } catch (error) {
        console.error("Error cloning thread:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [markThreadAsNew, handleThreadSelect]
  );

  const handleMoveToProject = useCallback(
    async (thread, targetWorkspaceId, sourceWorkspaceId = null) => {
      try {
        setIsLoading(true);

        // CRITICAL FIX: Don't rely solely on thread.workspace_id
        // The thread might have a stale workspace_id but not actually be in that workspace's
        // workspace_threads array (which is why it appears in the Chats section).
        // Check if the thread is actually in any workspace's workspace_threads array.
        const actualWorkspaceId =
          workspaces.find((ws) => ws.workspace_threads?.includes(thread.id))
            ?.id || null;

        const currentWorkspaceId = sourceWorkspaceId || actualWorkspaceId;

        // If moving to the same workspace, do nothing
        if (currentWorkspaceId === targetWorkspaceId) {
          return;
        }

        // Atomically move the chat: removes it from all workspaces, adds it to the
        // target, updates workspace_id + display_order, shifts siblings, and updates
        // metadata — all in a single transaction that rolls back on error. Replaces
        // the previous multi-step manual sequence, which could orphan a chat (its
        // workspace_id got set but it was never added to the target's
        // workspace_threads array, so it vanished from every project and the move
        // reported failure). Same RPC drag-and-drop and the project-list menu use.
        const newDisplayOrder = 0;
        const userId = useAuthStore.getState().user?.id || thread.user_id;
        const { data: result, error: rpcError } = await supabase.rpc(
          "move_thread_to_workspace_atomic",
          {
            p_thread_id: thread.id,
            p_target_workspace_id: targetWorkspaceId,
            p_display_order: newDisplayOrder,
            p_user_id: userId,
          }
        );

        if (rpcError) throw rpcError;
        if (result?.success !== true) {
          throw new Error("Move operation did not complete successfully");
        }

        // Update local workspace state - ensure thread is removed from ALL workspaces
        const updatedWorkspaces = workspaces.map((ws) => {
          const hasThread = (ws.workspace_threads || []).includes(thread.id);

          // Remove thread from all workspaces except target
          if (hasThread && ws.id !== targetWorkspaceId) {
            return {
              ...ws,
              workspace_threads: (ws.workspace_threads || []).filter(
                (id) => id !== thread.id
              ),
            };
          }

          // Add thread to target workspace if not already there
          if (ws.id === targetWorkspaceId && !hasThread) {
            return {
              ...ws,
              workspace_threads: [...(ws.workspace_threads || []), thread.id],
            };
          }

          return ws;
        });

        setWorkspaces(updatedWorkspaces);

        // Update thread store
        const updatedThread = {
          ...thread,
          workspace_id: targetWorkspaceId,
          display_order: newDisplayOrder,
          metadata: {
            ...thread.metadata,
            workspace_id: targetWorkspaceId,
            moved_to_history_at: targetWorkspaceId
              ? null
              : new Date().toISOString(),
          },
        };

        useThreadStore.setState((state) => {
          const updatedThreads = state.threads.map((t) =>
            t.id === thread.id ? updatedThread : t
          );
          return { threads: updatedThreads };
        });

        // Refresh threads to ensure history section updates
        await refreshThreadsAndCleanup();

        // Show success toast
        const workspace = workspaces.find((ws) => ws.id === targetWorkspaceId);
        toast({
          title: "Chat moved successfully",
          description: `Chat moved to ${workspace?.name || "project"}`,
        });
      } catch (error) {
        console.error("Error moving thread to project:", error);
        toast({
          title: "Error moving chat",
          description: "Failed to move chat to project. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [workspaces, setWorkspaces, toast]
  );

  return {
    // State
    isLoading,
    openDropdownId,

    // Setters
    setOpenDropdownId,

    // Actions
    handleThreadSelect,
    handleThreadDelete,
    handleThreadRenameSubmit,
    startRenaming,
    handleThreadRenameCancel,
    handleToggleBookmark,
    handleThreadClone,
    handleMoveToProject,

    // Dialog state
    threadRenameDialogOpen,
    threadToRename,
    newThreadName,
    isRenamingThread,
    setThreadRenameDialogOpen,
    setNewThreadName,
  };
}
